// Deps
import { Dispatch, DragEvent, useState } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  ReactFlowInstance,
} from '@xyflow/react';

// Styles
import './index.scss';
import '@xyflow/react/dist/style.css';

// Components
import SimulatorNode from './nodes/SimulatorNode';
import TimelineViewerNode from './nodes/TimelineViewerNode';
import ButtonNode from './nodes/ButtonNode';
import { GlobalAction, IGlobalState } from '../../states/GlobalState';
import { GlobalActionTypeList } from '../../states/GlobalState';
import { RealtimeClient } from '../../services/realtime-client';
import { UbiverseClient, UbiverseEventTopic } from '../../types';


const customNodeTypes = {
  simulatorNode: SimulatorNode,
  timelineViewerNode: TimelineViewerNode,
  buttonNode: ButtonNode
};

interface UbiverseProps {
  state: IGlobalState;
  dispatch: Dispatch<GlobalAction>;
  realtimeClient: RealtimeClient;
  sessionList: UbiverseClient[];
  onSelectSimulatorClient: (nodeId: string, clientId: string | null) => void;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
}

const Ubiverse = ({
  state,
  dispatch,
  realtimeClient,
  sessionList,
  onSelectSimulatorClient,
  onNodesChange,
  onEdgesChange,
  onConnect
}: UbiverseProps) => {
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  const createButtonNode = (position: { x: number; y: number }) => {
    const nodeId = `button-node--${new Date().getTime()}`;
    dispatch({
      type: GlobalActionTypeList.AddNode,
      payload: {
        id: nodeId,
        type: 'buttonNode',
        position,
        data: {
          label: 'Send event',
          onClick: (topic: UbiverseEventTopic, payload: string) => {
            let parsedPayload: any = payload;
            try {
              parsedPayload = payload ? JSON.parse(payload) : payload;
            } catch {
              parsedPayload = payload;
            }
            realtimeClient.emitEvent(topic, parsedPayload);
          }
        },
        deletable: true,
        connectable: true
      }
    });
  }

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!reactFlowInstance) return;
    const nodeType = event.dataTransfer.getData('application/ubiverse-node-type');
    const position = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });

    if (nodeType === 'timelineViewerNode') {
      const sequenceId = `sequence--${new Date().getTime()}`;
      dispatch({
        type: GlobalActionTypeList.AddSequence,
        payload: {
          id: sequenceId,
          name: 'Untitled timeline',
          isMuted: false,
          notes: []
        }
      });
      dispatch({
        type: GlobalActionTypeList.AddNode,
        payload: {
          id: sequenceId,
          type: 'timelineViewerNode',
          position,
          data: { id: sequenceId, label: 'Untitled timeline', state, dispatch, realtimeClient },
          deletable: true,
          connectable: true
        }
      });
      return;
    }

    if (nodeType === 'simulatorNode') {
      const nodeId = `simulator--${new Date().getTime()}`;
      dispatch({
        type: GlobalActionTypeList.AddNode,
        payload: {
          id: nodeId,
          type: 'simulatorNode',
          position,
          data: {
            label: 'Device',
            sessionList,
            selectedClientId: null,
            onSelectClient: onSelectSimulatorClient,
          },
          deletable: true,
          connectable: true
        }
      });
      return;
    }

    if (nodeType === 'buttonNode') {
      createButtonNode(position);
    }
  }

  return (
    <div className="component--ubiverse">
      <div className="component--ubiverse__container">
        <ReactFlow
          onInit={setReactFlowInstance}
          nodes={state.nodes}
          edges={state.edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDragOver={onDragOver}
          onDrop={onDrop}
          snapToGrid={true}
          nodeTypes={customNodeTypes}
        >
          <Controls />
          <Background variant={'dots' as BackgroundVariant} gap={12} size={0.5} />
        </ReactFlow>
      </div>
    </div>
  );
}

export default Ubiverse;
